import React, { useState, useEffect, useRef, useCallback } from "react";
import StudioHero from "../../components/StudioHero";
import StudioGenerateButton from "../../components/StudioGenerateButton";
import {
  runStudioWorkflow,
  getStudioWorkflowJob,
  getStudioWorkflowJobDetail,
} from "../../../../../actions/studios";
import { getUroSocket } from "../../../../../services/URO";
import { JobDetailModal } from "../../../../../components/JobDetailView";
import {
  GENDERS, ETHNICITIES, SKIN_COLORS, EYE_COLORS,
  SKIN_CONDITIONS, BODY_TYPES, HAIR_STYLES_MALE, HAIR_STYLES_FEMALE,
  MARKINGS, BREAST_SIZES, THIGH_SIZES, NECK_LENGTHS,
} from "./options";
import "./CharacterCreatorStudio.css";

const WORKFLOW_ID = "1fd70019-eb4d-482f-9611-5f0fc45b2dcc";

// ── Selectors ────────────────────────────────────────────────────

function ThumbnailSelector({ label, options, value, onChange, disabled }) {
  return (
    <div className="cc-field">
      <span className="cc-field-label">{label}</span>
      <div className="cc-thumbnails">
        {options.map((opt) => (
          <button
            key={opt.value}
            className={`cc-thumb ${value === opt.value ? "cc-thumb-active" : ""}`}
            onClick={() => onChange(opt.value)}
            type="button"
            title={opt.label}
            disabled={disabled}
          >
            {opt.thumbnail ? (
              <img src={opt.thumbnail} alt={opt.label} draggable={false} />
            ) : opt.hex ? (
              <div className="cc-thumb-swatch" style={{ backgroundColor: opt.hex }} />
            ) : (
              <span className="cc-thumb-text-only">{opt.label}</span>
            )}
            <span className="cc-thumb-label-overlay">{opt.label}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

function ChipSelector({ label, options, value, onChange, disabled }) {
  return (
    <div className="cc-field">
      <span className="cc-field-label">{label}</span>
      <div className="cc-chips">
        {options.map((opt) => (
          <button
            key={opt.value}
            className={`cc-chip ${value === opt.value ? "cc-chip-active" : ""}`}
            onClick={() => onChange(opt.value)}
            type="button"
            disabled={disabled}
          >
            {opt.label}
          </button>
        ))}
      </div>
    </div>
  );
}

// ── Main Component ───────────────────────────────────────────────

export default function CharacterCreatorStudio({ studio }) {
  // Selections
  const [gender, setGender] = useState("female");
  const [ethnicity, setEthnicity] = useState(null);
  const [skinColor, setSkinColor] = useState(null);
  const [eyeColor, setEyeColor] = useState(null);
  const [skinCondition, setSkinCondition] = useState("none");
  const [body, setBody] = useState(null);
  const [hair, setHair] = useState(null);
  const [marking, setMarking] = useState("none");
  const [breastSize, setBreastSize] = useState("medium");
  const [thighSize, setThighSize] = useState("normal");
  const [neckLength, setNeckLength] = useState("medium");

  // Multi-generation queue: [{id, status, imageUrl, jobData}]
  const [generations, setGenerations] = useState([]);
  const [selectedGenIndex, setSelectedGenIndex] = useState(-1);

  // Image viewer modal
  const [viewerData, setViewerData] = useState(null);

  const socketRef = useRef(null);
  const hairOptions = gender === "male" ? HAIR_STYLES_MALE : HAIR_STYLES_FEMALE;

  // Currently selected generation
  const selectedGen = selectedGenIndex >= 0 ? generations[selectedGenIndex] : null;
  const anyRunning = generations.some((g) => g.status === "running");

  // ── Socket + polling for ALL active generations ─────────────────

  useEffect(() => {
    const socket = getUroSocket();
    if (!socket) return;
    socketRef.current = socket;

    const handleComplete = async (payload) => {
      const jobId = payload?.workflow_job_id;
      if (!jobId) return;
      setGenerations((prev) => {
        const idx = prev.findIndex((g) => g.id === jobId);
        if (idx < 0) return prev;
        // Fetch the full job for the modal + extract image
        fetchAndUpdateGeneration(jobId);
        return prev;
      });
    };

    const handleFailed = (payload) => {
      const jobId = payload?.workflow_job_id;
      if (!jobId) return;
      setGenerations((prev) =>
        prev.map((g) =>
          g.id === jobId
            ? { ...g, status: "failed", error: payload?.failed_message || payload?.error || "Failed" }
            : g
        )
      );
    };

    const handleStepComplete = (payload) => {
      const jobId = payload?.workflow_job_id;
      if (!jobId) return;
      if (payload?.node_id === "llm-enhancer") {
        setGenerations((prev) =>
          prev.map((g) =>
            g.id === jobId ? { ...g, enhancerDone: true } : g
          )
        );
      }
    };

    socket.on("workflow:complete", handleComplete);
    socket.on("workflow:failed", handleFailed);
    socket.on("workflow:step:complete", handleStepComplete);

    return () => {
      socket.off("workflow:complete", handleComplete);
      socket.off("workflow:failed", handleFailed);
      socket.off("workflow:step:complete", handleStepComplete);
    };
  }, []);

  // Polling for running generations
  useEffect(() => {
    if (!anyRunning) return;

    const pollInterval = setInterval(async () => {
      const running = generations.filter((g) => g.status === "running");
      for (const gen of running) {
        try {
          const detail = await getStudioWorkflowJobDetail(gen.id);
          if (!detail) continue;

          if (detail.steps) {
            const enhancerStep = detail.steps.find(
              (s) => s.workflow_step_id === "llm-enhancer" && s.status === "completed"
            );
            if (enhancerStep) {
              setGenerations((prev) =>
                prev.map((g) => g.id === gen.id ? { ...g, enhancerDone: true } : g)
              );
            }
          }

          if (detail.status === "completed") {
            fetchAndUpdateGeneration(gen.id);
          } else if (detail.status === "failed") {
            setGenerations((prev) =>
              prev.map((g) =>
                g.id === gen.id
                  ? { ...g, status: "failed", error: detail.error_message || "Failed" }
                  : g
              )
            );
          }
        } catch (_) {}
      }
    }, 5000);

    return () => clearInterval(pollInterval);
  }, [anyRunning, generations]);

  const fetchAndUpdateGeneration = useCallback(async (jobId) => {
    try {
      // Fetch the full workflow job (standard shape for the modal)
      const jobData = await getStudioWorkflowJob(jobId);
      const resultsArr = jobData?.results?.results || jobData?.results;
      const imageUrl =
        Array.isArray(resultsArr) && resultsArr.length > 0
          ? resultsArr[0].content || resultsArr[0].external_url
          : null;

      // Pass the job data as-is — the API returns {results: {results: [...]}}
      // which JobDetailModal's extractOutputResults expects for workflow jobs.
      const modalData = jobData;

      setGenerations((prev) =>
        prev.map((g) =>
          g.id === jobId
            ? { ...g, status: "completed", imageUrl, jobData: modalData }
            : g
        )
      );
    } catch (err) {
      setGenerations((prev) =>
        prev.map((g) =>
          g.id === jobId
            ? { ...g, status: "completed", imageUrl: null }
            : g
        )
      );
    }
  }, []);

  const canGenerate = !!ethnicity && !!skinColor && !!eyeColor && !!body && !!hair;

  const handleGenerate = async () => {
    if (!canGenerate) return;

    try {
      const runResult = await runStudioWorkflow(WORKFLOW_ID, {
        gender,
        ethnicity,
        skin_color: skinColor,
        eye_color: eyeColor,
        body,
        hair,
        skin_condition: skinCondition,
        marking,
        breast_size: gender === "female" ? breastSize : undefined,
        thigh_size: thighSize,
        neck_length: neckLength,
      });

      const newGen = {
        id: runResult.workflow_job_id,
        status: "running",
        imageUrl: null,
        jobData: null,
        enhancerDone: false,
        error: null,
      };

      setGenerations((prev) => [newGen, ...prev]);
      setSelectedGenIndex(0);
    } catch (err) {
      // Show error inline — don't add to queue
      alert(err.message || "Failed to start generation");
    }
  };

  const handleCardClick = (index) => {
    setSelectedGenIndex(index);
  };

  const handleImageClick = () => {
    if (!selectedGen?.jobData) return;
    setViewerData(selectedGen.jobData);
  };

  // What to show in the main canvas
  const mainGen = selectedGen;

  return (
    <div className="cc-studio">
      <StudioHero
        title={studio.name}
        subtitle="Studio"
        description={studio.description}
      />

      <div className="cc-layout">
        {/* ── Left: param selectors ──────────────────────────── */}
        <div className="cc-form-panel">
          <div className="cc-form-scroll">
            <ThumbnailSelector label="Gender" options={GENDERS} value={gender}
              onChange={(v) => { setGender(v); setHair(null); }} />
            <ChipSelector label="Ethnicity" options={ETHNICITIES} value={ethnicity} onChange={setEthnicity} />
            <ThumbnailSelector label="Skin Color" options={SKIN_COLORS} value={skinColor} onChange={setSkinColor} />
            <ThumbnailSelector label="Eye Color" options={EYE_COLORS} value={eyeColor} onChange={setEyeColor} />
            <ThumbnailSelector label="Physique" options={BODY_TYPES} value={body} onChange={setBody} />
            <ThumbnailSelector label="Hair Style" options={hairOptions} value={hair} onChange={setHair} />
            <ThumbnailSelector label="Skin Condition" options={SKIN_CONDITIONS} value={skinCondition} onChange={setSkinCondition} />
            <ThumbnailSelector label="Markings & Accessories" options={MARKINGS} value={marking} onChange={setMarking} />
            {gender === "female" && (
              <ChipSelector label="Breast Size" options={BREAST_SIZES} value={breastSize} onChange={setBreastSize} />
            )}
            <ChipSelector label="Thigh Size" options={THIGH_SIZES} value={thighSize} onChange={setThighSize} />
            <ChipSelector label="Neck Length" options={NECK_LENGTHS} value={neckLength} onChange={setNeckLength} />
          </div>

          <div className="cc-form-actions">
            <StudioGenerateButton
              onClick={handleGenerate}
              disabled={!canGenerate}
              loading={false}
            />
          </div>
        </div>

        {/* ── Right: canvas + generation strip ───────────────── */}
        <div className="cc-right-panel">
          <div className="cc-canvas-panel">
            {/* Empty state */}
            {!mainGen && (
              <div className="cc-empty">
                <div className="cc-empty-icon">🧑</div>
                <div className="cc-empty-text">Your character will appear here</div>
              </div>
            )}

            {/* Running */}
            {mainGen?.status === "running" && (
              <div className="cc-loading">
                <div className="cc-loading-spinner" />
                <div className="cc-loading-text">
                  {mainGen.enhancerDone ? "Generating character..." : "Planning character details..."}
                </div>
              </div>
            )}

            {/* Failed */}
            {mainGen?.status === "failed" && (
              <div className="cc-error">
                <div className="cc-error-icon">⚠</div>
                <div className="cc-error-text">{mainGen.error || "Generation failed"}</div>
              </div>
            )}

            {/* Completed — click to open modal */}
            {mainGen?.status === "completed" && mainGen.imageUrl && (
              <div className="cc-result" onClick={handleImageClick}>
                <img
                  src={mainGen.imageUrl}
                  alt="Generated character"
                  className="cc-result-image"
                  draggable={false}
                />
              </div>
            )}
          </div>

          {/* ── Generation strip at bottom ─────────────────── */}
          {generations.length > 0 && (
            <div className="cc-gen-strip">
              {generations.map((gen, idx) => (
                <button
                  key={gen.id}
                  className={`cc-gen-card ${idx === selectedGenIndex ? "cc-gen-card-active" : ""}`}
                  onClick={() => handleCardClick(idx)}
                  type="button"
                >
                  {gen.status === "running" && (
                    <div className="cc-gen-card-spinner" />
                  )}
                  {gen.status === "failed" && (
                    <div className="cc-gen-card-failed">⚠</div>
                  )}
                  {gen.status === "completed" && gen.imageUrl && (
                    <img src={gen.imageUrl} alt="" draggable={false} />
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      <JobDetailModal
        visible={!!viewerData}
        data={viewerData}
        onClose={() => setViewerData(null)}
      />
    </div>
  );
}
